import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { buildTheme, formatPrice } from './public/utils';
import SiteHeader from './public/components/SiteHeader';
import Breadcrumb from './public/components/Breadcrumb';
import ImageGallery from './public/components/ImageGallery';
import ProductInfo from './public/components/ProductInfo';
import RelatedSection from './public/components/RelatedSection';
import MobileBottomBar from './public/components/MobileBottomBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicProduct = () => {
  const { slug, productId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [variantGroups, setVariantGroups] = useState([]);

  const fetchProduct = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/public/shops/${slug}/products/${productId}`);
      setData(response.data);
      setVariantGroups(response.data.variantGroups || []);
      
      // Use manual upsell products if available, otherwise fallback to same category
      const upsells = response.data.upsellProducts || [];
      if (upsells.length > 0) {
        setRelatedProducts(upsells);
      } else {
        const catalogRes = await axios.get(`${API}/public/shops/${slug}`);
        const related = catalogRes.data.products
          .filter(p => p.categoryId === response.data.product.categoryId && p.id !== productId)
          .slice(0, 4);
        setRelatedProducts(related);
      }
    } catch (error) {
      toast.error('Produit non trouve');
    } finally {
      setLoading(false);
    }
  }, [slug, productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const trackEvent = async (type) => {
    try {
      await axios.post(`${API}/public/shops/${slug}/events`, { type, productId });
    } catch (e) {}
  };

  const handleWhatsApp = () => {
    trackEvent('WHATSAPP_CLICK');
    const phone = data.shop.whatsappNumber?.replace(/\s/g, '');
    const message = `Bonjour, je suis interesse(e) par "${data.product.name}". Est-il disponible ?`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleCall = () => {
    trackEvent('CALL_CLICK');
    window.location.href = `tel:${data.shop.phone}`;
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = `${data.product.name} - ${data.shop.name}`;
    if (navigator.share) {
      try { await navigator.share({ title, url }); } catch (e) {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Lien copie !');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#09090B' }}>
        <div className="w-10 h-10 rounded-full border-3 border-gray-700 border-t-gray-200 animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#09090B', color: '#fafafa' }}>
        <div className="text-center max-w-md">
          <Package className="w-16 h-16 mx-auto mb-4" style={{ opacity: 0.3 }} />
          <h1 className="text-2xl font-bold mb-3">Produit introuvable</h1>
          <Button onClick={() => navigate(`/s/${slug}`)} className="rounded-full">
            Retour au catalogue
          </Button>
        </div>
      </div>
    );
  }

  const { shop, product, category } = data;
  const theme = buildTheme(shop);
  const { accentColor } = theme;
  const images = product.images || [];
  const hasImages = images.length > 0;

  const activeVariantImage = Object.values(selectedVariants).find(opt => opt.imageUrl)?.imageUrl || null;
  const activeVariantPrice = Object.values(selectedVariants).find(opt => opt.priceCents != null)?.priceCents ?? null;
  const effectivePrice = activeVariantPrice !== null ? activeVariantPrice : product.priceCents;

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.pageBg, color: theme.textColor }} data-testid="public-product">
      <SiteHeader shop={shop} slug={slug} navigate={navigate} onShare={handleShare} theme={theme} />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumb shop={shop} category={category} product={product} slug={slug} navigate={navigate} theme={theme} />
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 pb-28 lg:pb-12">
          <ImageGallery 
            images={images}
            hasImages={hasImages}
            currentIndex={currentImageIndex}
            setCurrentIndex={setCurrentImageIndex}
            variantImageUrl={activeVariantImage}
            theme={theme}
          />
          
          <ProductInfo 
            product={product}
            category={category}
            shop={shop}
            accentColor={accentColor}
            formatPrice={formatPrice}
            effectivePrice={effectivePrice}
            variantGroups={variantGroups}
            selectedVariants={selectedVariants}
            onSelectVariant={(groupId, option) => {
              setSelectedVariants(prev => {
                if (prev[groupId]?.id === option.id) {
                  const next = { ...prev };
                  delete next[groupId];
                  return next;
                }
                return { ...prev, [groupId]: option };
              });
            }}
            onWhatsApp={handleWhatsApp}
            onCall={handleCall}
            onShare={handleShare}
            theme={theme}
          />
        </div>
        
        <RelatedSection 
          products={relatedProducts}
          slug={slug}
          accentColor={accentColor}
          formatPrice={formatPrice}
          navigate={navigate}
          theme={theme}
        />
      </div>
      
      <MobileBottomBar 
        shop={shop}
        accentColor={accentColor}
        onWhatsApp={handleWhatsApp}
        onCall={handleCall}
        theme={theme}
      />
    </div>
  );
};

export default PublicProduct;
